import "@src/init";

import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import Head from "next/head";
import Router from "next/router";
import { capitalize } from "lodash-es";

import KButton from "@kikoff/components/src/v1/buttons/KButton";
import { protoDate } from "@kikoff/utils/src/proto";
import { format } from "@kikoff/utils/src/string";

import Line from "@component/line";
import { GenericSpinner } from "@component/loading_indicators/loading_indicators";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import { FIRST_MONTH_DISCOUNT_PROMO } from "@constant/promos";
import { selectPromo } from "@feature/user";
import {
  getCreditServiceBasicLongMentalModelName,
  getCreditServicePremiumLongMentalModelName,
} from "@src/constants";
import { track } from "@util/analytics";
import { Events } from "@util/events";
import { l10nResources } from "@util/l10n";

import parachutingGift from "./parachuting_gift.svg";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `promo-view-special-offer = Special offer
promo-view-continue = Next
promo-view-first-month-title = Get your first month of Kikoff for 50% off*
promo-view-last-month-title = Get a month of free Credit Building*
promo-view-first-month-copy = Start building your credit score with Kikoff. Trusted by millions. Fast and effective.
promo-view-first-month-disclaimer = *Offer ends {$formattedOfferEndDate}. Offer applies to the first month of purchase of a 12-month Kikoff {$creditService} or Kikoff {$creditServicePremium} plan. Each subsequent month is billed at $5/month for {$kikoffCreditService} or $20/month for {$kikoffCreditService} Premium. Terms of offer are subject to change.
promo-view-last-month-disclaimer = *Offer ends {$formattedOfferEndDate}. Offer applies to the last month of purchase of a 12-month Kikoff {$creditService} plan. The first 11 months are billed at $5/month or $20/month for Kikoff {$creditServicePremium}. Terms of offer are subject to change.
`,
  es: `promo-view-special-offer = Oferta especial
promo-view-continue = Siguiente
promo-view-first-month-title = Obtén tu primer mes de Kikoff por {$firstMonthPrice}*
promo-view-first-month-copy = Comience a construir su puntaje crediticio con Kikoff. Con la confianza de millones. Rápido y eficaz.
promo-view-first-month-disclaimer = *La oferta finaliza el {$formattedOfferEndDate}. La oferta se aplica al primer mes de la compra de un plan de {$creditServicePremium} Kikoff de 12 meses. Cada mes subsiguiente se factura a $20/mes. Los términos de la oferta están sujetos a cambios.
promo-view-last-month-title = Obtenga un mes de creación de crédito gratis*
promo-view-last-month-disclaimer = *La oferta finaliza el {$formattedOfferEndDate}. La oferta se aplica al último mes de la compra de un plan de {$creditService} Kikoff de 12 meses. Los primeros 11 meses se facturan a $5/mes o factura a $20/mes. Los términos de la oferta están sujetos a cambios.
`,
});

function ContinueButton() {
  return (
    <div className={styles["button-wrapper"]}>
      <KButton
        size="full-width"
        variant="black"
        onClick={() => {
          track.tap("Promos - Email Incentive Promo - CTA");
          Router.push("/dashboard/store/get-started");
        }}
      >
        {l10n.getString("promo-view-continue")}
      </KButton>
    </div>
  );
}

function PromoContents({ promo }) {
  const endDate = Object.assign(protoDate(promo.endAt), { zone: "PST" });

  const formattedOfferEndDate = `${format.date(
    endDate,
    "Mmmm d, yyyy"
  )} at 11:59pm PST`;

  const creditService = getCreditServiceBasicLongMentalModelName();
  const creditServicePremium = getCreditServicePremiumLongMentalModelName();

  return (
    <>
      <img
        alt="parachuting gift"
        className={styles["parachuting-gift"]}
        src={parachutingGift.src}
      />
      <section className={styles["promo-contents"]}>
        <div className={styles["special-offer-header"]}>
          {l10n.getString("promo-view-special-offer")}
        </div>
        <div className={styles["promo-header"]}>
          {promo.name === FIRST_MONTH_DISCOUNT_PROMO
            ? l10n.getString("promo-view-first-month-title")
            : l10n.getString("promo-view-last-month-title")}
        </div>
        <div className={`text:large ${styles["promo-main-copy"]}`}>
          {l10n.getString("promo-view-first-month-copy")}
        </div>
        <div className="text:mini">
          {promo.name === FIRST_MONTH_DISCOUNT_PROMO
            ? l10n.getString("promo-view-first-month-disclaimer", {
                formattedOfferEndDate,
                creditService,
                creditServicePremium,
              })
            : l10n.getString("promo-view-last-month-disclaimer", {
                formattedOfferEndDate,
                creditService,
                creditServicePremium,
              })}
        </div>
        <div className={styles["promo-divider"]} />
        <Line />
        <section className={styles["promo-footer"]}>
          <ContinueButton />
        </section>
      </section>
    </>
  );
}

function PromoOffer({ promoName }) {
  const promo = useSelector(selectPromo(promoName));

  useEffect(() => {
    track(
      `Promos - Email Incentive Promo - ${promoName
        .split("_")
        .map((word) => capitalize(word))
        .join(" ")}` as keyof Events
    );
  }, []);

  if (!promo) {
    return (
      <section className={styles["promo-contents"]}>
        <div className={styles["spinner-wrapper"]}>
          <GenericSpinner center />;
        </div>
      </section>
    );
  }

  return (
    <>
      <Head>
        <title>Kikoff Credit Builder | The Easiest Way To Build Credit</title>
      </Head>
      <body className={styles["promo-view-body"]}>
        <StreamlinedHeader />
        <PromoContents promo={promo} />
      </body>
    </>
  );
}

export default PromoOffer;
